import type { FretboardPattern } from "./fingeringEngine";
import { fingeringToNeckPositions } from "./fingeringEngine";
import { getNoteAtPosition, type NeckPosition, type NoteName } from "./guitarEngine";

export type ExploreNpsSelection = "full" | string;

/** Alcance de la secuencia 3NPS en sandbox. */
export type ExplorePlaybackRange = "octave" | "extend";

export const NPS_POSITION_LABELS = [
  "POS I",
  "POS II",
  "POS III",
  "POS IV",
  "POS V",
  "POS VI",
  "POS VII",
] as const;

export function positionKey(string: number, fret: number): string {
  return `${string}-${fret}`;
}

export function fingeringNoteKeys(fp: FretboardPattern): Set<string> {
  return new Set(fp.notes.map((n) => positionKey(n.string, n.fret)));
}

/** Patrones 3NPS canónicos: ~3 notas por cuerda, cobertura amplia de la escala. */
export function selectCanonical3NpsFingerings(
  fingerings: FretboardPattern[]
): FretboardPattern[] {
  const candidates = fingerings.filter((f) => {
    if (f.type !== "fingering") return false;
    const perString = new Map<number, number>();
    for (const n of f.notes) {
      perString.set(n.string, (perString.get(n.string) ?? 0) + 1);
    }
    if (perString.size < 5) return false;
    const tripleStrings = [...perString.values()].filter((c) => c === 3).length;
    return tripleStrings >= 4 && f.notes.length >= 14 && (f.coverage ?? 0) >= 5;
  });

  const byBucket = new Map<number, FretboardPattern>();
  for (const c of [...candidates].sort((a, b) => a.position - b.position)) {
    const bucket = Math.floor(c.position / 2);
    const prev = byBucket.get(bucket);
    if (!prev || (c.comfortScore ?? 0) > (prev.comfortScore ?? 0)) {
      byBucket.set(bucket, c);
    }
  }

  let ordered = [...byBucket.values()].sort((a, b) => a.position - b.position);

  if (ordered.length > 7) {
    const picks: FretboardPattern[] = [];
    for (let i = 0; i < 7; i++) {
      const idx = Math.round((i * (ordered.length - 1)) / 6);
      picks.push(ordered[idx]);
    }
    ordered = picks;
  }

  if (ordered.length < 7) {
    const seen = new Set(ordered.map((f) => f.id));
    for (const f of fingerings) {
      if (ordered.length >= 7) break;
      if (seen.has(f.id)) continue;
      if (f.notes.length >= 12) {
        ordered.push(f);
        seen.add(f.id);
      }
    }
  }

  return ordered.slice(0, 7);
}

type NeckPosWithMidi = NeckPosition & { midi: number };

/** Una octava completa: hasta grado 8 o tónica en la octava siguiente (inclusive). */
function limitPatternRunToOctave(
  patternRun: NeckPosWithMidi[],
  tonicMidi: number,
  rootNote: NoteName
): NeckPosWithMidi[] {
  const octaveTop = tonicMidi + 12;
  const idx8 = patternRun.findIndex((p) => p.degree === "8");
  if (idx8 >= 0) return patternRun.slice(0, idx8 + 1);

  const idxHighRoot = patternRun.findIndex(
    (p) => p.midi >= octaveTop && (p.degree === "R" || p.note === rootNote)
  );
  if (idxHighRoot >= 0) return patternRun.slice(0, idxHighRoot + 1);

  return patternRun.filter((p) => p.midi <= octaveTop);
}

/** Omite repetir la misma altura al cambiar de cuerda (mismo MIDI consecutivo). */
export function dedupeConsecutiveSamePitch(positions: NeckPosWithMidi[]): NeckPosition[] {
  const out: NeckPosition[] = [];
  let lastMidi: number | null = null;
  for (const p of positions) {
    if (lastMidi !== null && p.midi === lastMidi) continue;
    const { midi: _midi, ...neck } = p;
    out.push(neck);
    lastMidi = p.midi;
  }
  return out;
}

/**
 * Secuencia ascendente desde la tónica más grave (caja o mástil completo).
 * `octave`: solo una octava desde la tónica. `extend`: sigue con el resto del patrón.
 */
export function neckPositionsToPlaybackSequence(
  positions: NeckPosition[],
  rootNote: NoteName,
  range: ExplorePlaybackRange = "octave"
): NeckPosition[] {
  const withMidi: NeckPosWithMidi[] = positions.map((p) => ({
    ...p,
    midi: getNoteAtPosition(p.string, p.fret).midi,
    highlight: (p.degree === "R" || p.note === rootNote ? "root" : "scale") as NeckPosition["highlight"],
  }));

  const ascending = [...withMidi].sort((a, b) => a.midi - b.midi);

  const tonics = ascending.filter((p) => p.degree === "R" || p.note === rootNote);
  const lowestTonic = tonics[0];
  if (!lowestTonic) return dedupeConsecutiveSamePitch(ascending);

  const tonicMidi = lowestTonic.midi;
  const tonicKey = positionKey(lowestTonic.string, lowestTonic.fret);

  let patternRun = ascending.filter(
    (p) => p.midi > tonicMidi && positionKey(p.string, p.fret) !== tonicKey
  );

  if (range === "octave") {
    patternRun = limitPatternRunToOctave(patternRun, tonicMidi, rootNote);
  }

  const raw: NeckPosWithMidi[] = [
    { ...lowestTonic, highlight: "root" as const },
    ...patternRun,
  ];

  return dedupeConsecutiveSamePitch(raw);
}

/** Secuencia 3NPS / CAGED: delega en {@link neckPositionsToPlaybackSequence}. */
export function fingeringToPlaybackSequence(
  fp: FretboardPattern,
  rootNote: NoteName,
  range: ExplorePlaybackRange = "octave"
): NeckPosition[] {
  return neckPositionsToPlaybackSequence(fingeringToNeckPositions(fp), rootNote, range);
}

/** Diagonal lifts (px) para el glifo de 3 puntos del selector. */
export function npsGlyphLifts(noteCount: number): number[] {
  if (noteCount <= 1) return [0];
  if (noteCount === 2) return [0, 10];
  const maxLift = 14;
  return [0, Math.round(maxLift / 2), maxLift];
}
